import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const NONE_OPTION = "__none__";

export default function ImportarPlanilha() {
  const { user } = useAuth();
  const isEmpresa = user?.role === "empresa";
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState(isEmpresa ? user.empresa_id : "");
  const [tipoPlanilha, setTipoPlanilha] = useState("pedido");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [pendentes, setPendentes] = useState([]);

  // Mapeamento inteligente
  const [analise, setAnalise] = useState(null); // { colunas, preview, mapeamento_sugerido, campos_sistema, total_linhas }
  const [mapeamento, setMapeamento] = useState({}); // {sistema_field: csv_column}
  const [modelos, setModelos] = useState([]);
  const [modeloSelecionado, setModeloSelecionado] = useState("");
  const [salvarComoModelo, setSalvarComoModelo] = useState(false);
  const [nomeModelo, setNomeModelo] = useState("");
  const [descricaoModelo, setDescricaoModelo] = useState("");
  const [campos, setCampos] = useState([]);
  const [editandoModelo, setEditandoModelo] = useState(null);
  const [preview, setPreview] = useState(null); // {ficha, linha_bruta, total_linhas, alertas}
  const [carregandoPreview, setCarregandoPreview] = useState(false);
  const [conferencia, setConferencia] = useState(null);
  const [dupDecisoes, setDupDecisoes] = useState({});
  const [valorCorrecoes, setValorCorrecoes] = useState({}); // {linha: "valor corrigido"}
  const [carregandoConf, setCarregandoConf] = useState(false);
  const [onboarding, setOnboarding] = useState(null);

  useEffect(() => {
    if (isEmpresa) api.get("/empresa/onboarding-status").then(r => setOnboarding(r.data)).catch(() => {});
  }, [isEmpresa]);
  const bloqueadoOnboarding = isEmpresa && onboarding && !onboarding.completo;
  const bloqueadoPublicacao = isEmpresa && onboarding && onboarding.completo && !onboarding.condicoes_publicadas;

  const base = window.location.origin;

  useEffect(() => {
    api.get("/empresas").then(r => setEmpresas(r.data));
    api.get("/importacao/campos-sistema").then(r => setCampos(r.data));
    loadPendentes();
  }, []);

  useEffect(() => {
    if (empresaId) loadModelos(empresaId);
    else setModelos([]);
    setModeloSelecionado("");
  }, [empresaId]);

  const loadPendentes = () => api.get("/planilhas/pendentes").then(r => setPendentes(r.data)).catch(() => {});

  const loadModelos = async (eid) => {
    try {
      const r = await api.get(`/empresas/${eid}/modelos-importacao`);
      setModelos(r.data);
    } catch (e) { setModelos([]); }
  };

  // 1. Analisa CSV após upload (sem importar)
  const analisar = async () => {
    if (bloqueadoOnboarding) { toast.error("Complete o cadastro da empresa antes de importar (veja as pendências acima)"); return; }
    if (bloqueadoPublicacao) { toast.error("Publique suas condições para a rede antes de importar (Comercial → Publicar Condições)"); return; }
    if (tipoPlanilha === "particular") { toast.error("Planilha de Cliente Particular: use a página Cliente Particular"); return; }
    if (!empresaId || !file) { toast.error("Selecione empresa e arquivo"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("empresa_id", empresaId);
      const r = await api.post("/planilhas/analyze", fd);
      setAnalise(r.data);
      setMapeamento(r.data.mapeamento_sugerido || {});
      setModeloSelecionado("");
      setConferencia(null);
      setValorCorrecoes({});
      // 1ª importação da empresa (sem modelo salvo): já marca "salvar como modelo"
      const temModeloSalvo = (modelos || []).some(m => !m.auto);
      if (!temModeloSalvo && !r.data.modelo_aplicado) {
        setSalvarComoModelo(true);
        setNomeModelo(`Modelo padrão — 1ª importação`);
        toast.info("Primeira importação desta empresa: o mapeamento confirmado será salvo como modelo");
      }
      if (r.data.modelo_aplicado) {
        toast.success(`🧠 Mapeamento lembrado: modelo "${r.data.modelo_aplicado.nome}" aplicado automaticamente (${r.data.modelo_aplicado.campos_aplicados} campos)`);
      } else {
        toast.success(`${r.data.colunas.length} colunas detectadas · ${r.data.total_linhas} linhas`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  // 2. Aplica modelo pré-existente
  const aplicarModelo = (mid) => {
    setModeloSelecionado(mid);
    if (mid === NONE_OPTION || !mid) { return; }
    const m = modelos.find(x => x.id === mid);
    if (m) { setMapeamento(m.mapeamento || {}); toast.success(`Modelo "${m.nome}" aplicado`); }
  };

  const setMap = (sistemaKey, csvCol) => {
    setConferencia(null);
    setMapeamento(prev => {
      const next = { ...prev };
      if (!csvCol || csvCol === NONE_OPTION) delete next[sistemaKey];
      else next[sistemaKey] = csvCol;
      return next;
    });
  };

  const correcoesPreenchidas = () =>
    Object.fromEntries(Object.entries(valorCorrecoes).filter(([, v]) => String(v || "").trim()));

  const conferirFinanceiro = async (comCorrecoes = false) => {
    if (!file || !empresaId) { toast.error("Selecione empresa e arquivo"); return; }
    setCarregandoConf(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("empresa_id", empresaId);
      fd.append("mapeamento_json", JSON.stringify(mapeamento));
      const corr = correcoesPreenchidas();
      if (comCorrecoes && Object.keys(corr).length) fd.append("correcoes_json", JSON.stringify(corr));
      const r = await api.post("/planilhas/conferencia-financeira", fd);
      setConferencia(r.data);
      if (r.data.pode_importar) toast.success("Conferência OK — cálculos validados, pode importar");
      else toast.error(`${r.data.erros.length} erro(s) encontrados — corrija antes de importar`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCarregandoConf(false); }
  };

  const verPreviewFicha = async () => {
    if (!file || !empresaId) { toast.error("Selecione empresa e arquivo"); return; }
    if (!mapeamento.nome) { toast.error("Mapeie ao menos o Nome do cliente"); return; }
    setCarregandoPreview(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapeamento_json", JSON.stringify(mapeamento));
      fd.append("empresa_id", empresaId);
      const r = await api.post("/planilhas/preview-ficha", fd);
      setPreview(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCarregandoPreview(false); }
  };

  // 3. Importa de fato
  const importar = async () => {
    if (!file || !empresaId) { toast.error("Selecione empresa e arquivo"); return; }
    if (!mapeamento.nome) { toast.error("Mapeie ao menos o Nome do cliente"); return; }
    if (!conferencia?.pode_importar) { toast.error("Faça a Conferência Financeira antes de importar"); return; }
    setLoading(true);
    try {
      // Se marcou "salvar como modelo", cria primeiro
      let modeloUsado = modeloSelecionado && modeloSelecionado !== NONE_OPTION ? modeloSelecionado : null;
      if (salvarComoModelo && !modeloUsado) {
        if (!nomeModelo.trim()) { toast.error("Informe um nome para o modelo"); setLoading(false); return; }
        const r = await api.post(`/empresas/${empresaId}/modelos-importacao`, {
          nome: nomeModelo, descricao: descricaoModelo,
          mapeamento, separador: analise?.separador || ",",
          colunas_amostra: analise?.colunas || [],
        });
        modeloUsado = r.data.id;
        await loadModelos(empresaId);
        toast.success("Modelo salvo");
      }

      const fd = new FormData();
      fd.append("file", file);
      fd.append("empresa_id", empresaId);
      fd.append("tipo_planilha", tipoPlanilha);
      if (modeloUsado) fd.append("modelo_id", modeloUsado);
      else fd.append("mapeamento_json", JSON.stringify(mapeamento));
      if (Object.keys(dupDecisoes).length) fd.append("duplicidades_json", JSON.stringify(dupDecisoes));
      const corrImp = correcoesPreenchidas();
      if (Object.keys(corrImp).length) fd.append("correcoes_json", JSON.stringify(corrImp));
      const r = await api.post("/planilhas/import", fd);
      setResultado(r.data);
      toast.success(`${r.data.linhas_importadas} OS gerada(s) de ${r.data.linhas_planilha ?? r.data.linhas_importadas} linha(s)`
        + (r.data.clientes_agrupados ? ` · ${r.data.clientes_agrupados} item(ns) agrupados por cliente` : "")
        + ` · ${r.data.whatsapp_enviados || 0} WhatsApp enviado(s)`);
      if (r.data.whatsapp_alertas?.length) {
        toast.warning(`${r.data.whatsapp_alertas.length} cliente(s) NÃO receberam WhatsApp — veja os alertas na aba Pendentes`);
      }
      loadPendentes();
      // Reset
      setAnalise(null); setMapeamento({}); setFile(null);
      setSalvarComoModelo(false); setNomeModelo(""); setDescricaoModelo("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const removerModelo = async (mid) => {
    if (!window.confirm("Remover este modelo?")) return;
    await api.delete(`/empresas/${empresaId}/modelos-importacao/${mid}`);
    toast.success("Modelo removido");
    loadModelos(empresaId);
  };

  const salvarEdicaoModelo = async () => {
    if (!editandoModelo) return;
    await api.put(`/empresas/${empresaId}/modelos-importacao/${editandoModelo.id}`, {
      nome: editandoModelo.nome, descricao: editandoModelo.descricao,
      mapeamento: editandoModelo.mapeamento,
    });
    toast.success("Modelo atualizado");
    setEditandoModelo(null);
    loadModelos(empresaId);
  };

  const empresa = empresas.find(e => e.id === empresaId);
  const empresaNome = empresa?.nome || "{EMPRESA}";

  const baixarExemplo = () => {
    const csv = `cpf,nome,telefone,endereco,cidade,estado,produto,quantidade,cor\n12345678901,João da Silva,11999999999,Rua A 100,São Paulo,SP,Guarda-roupa 6 portas,1,Branco`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = "exemplo-planilha-ativaja.csv"; a.click(); URL.revokeObjectURL(url);
  };

  const gerarMensagem = (item) => {
    const link = `${base}/atendimento/${item.token}`;
    const nome = (item.nome || "Cliente").split(" ")[0];
    const empresaMsg = item.empresa_nome || empresaNome || "MontaJá";
    return `Olá, ${nome}! 😊\n\nSeu pedido já está disponível para agendamento da montagem.\nPara facilitar, clique no link abaixo e informe até 5 datas em que você tenha disponibilidade para receber nossa equipe:\n\n🔗 ${link}\n\nPor esse mesmo link você também poderá:\n• acompanhar sua Ordem de Serviço;\n• solicitar um reagendamento, se necessário;\n• enviar informações sobre o atendimento.\n\nOrdem de Serviço: ${item.os_numero || "—"}\n\nAssim que recebermos sua disponibilidade, nossa equipe fará o possível para agendar a melhor data para você.\n\nAgradecemos pela confiança e ficamos à disposição! 🤝\n\nEquipe ${empresaMsg}`;
  };

  const enviarWhats = (item) => {
    const tel = (item.telefone || "").replace(/\D/g, "");
    const msg = encodeURIComponent(gerarMensagem(item));
    window.open(`https://wa.me/55${tel}?text=${msg}`, "_blank");
    api.post(`/planilhas/${item.id}/marcar-enviado`).then(loadPendentes);
  };

  const copiarMsg = (item) => {
    navigator.clipboard.writeText(gerarMensagem(item));
    toast.success("Mensagem copiada");
    api.post(`/planilhas/${item.id}/marcar-enviado`).then(loadPendentes);
  };

  const STATUS_COLORS = {
    gerado: "bg-secondary text-foreground",
    enviado: "bg-[#FFCC00] text-foreground",
    entregue: "bg-[#FFCC00] text-foreground",
    lido: "bg-[#00A859] text-white",
    acessado: "bg-[#142766] text-white",
    alerta_envio: "bg-[#E60000] text-white",
  };
  const MOTIVO_ALERTA = {
    sem_numero: "Sem número de telefone",
    numero_invalido: "Número inválido",
    telefone_fixo_somente_ligacao: "Telefone fixo — só ligação",
    falha_envio_whatsapp: "Falha no envio (WhatsApp)",
  };

  // Colunas que ainda não foram mapeadas
  const colunasUsadas = useMemo(() => new Set(Object.values(mapeamento).filter(Boolean)), [mapeamento]);

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-8">
        <div className="label-cap">Operacional</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Importar Planilha</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Mapeie as colunas da sua planilha para os campos do sistema <strong>uma única vez</strong> e reutilize sempre.
        </p>
      </div>

      <Tabs defaultValue="importar">
        <TabsList className="rounded-none">
          <TabsTrigger value="importar" data-testid="tab-importar">Importar</TabsTrigger>
          <TabsTrigger value="modelos" data-testid="tab-modelos">Modelos ({modelos.length})</TabsTrigger>
          <TabsTrigger value="pendentes" data-testid="tab-pendentes">Pendentes ({pendentes.filter(p => p.link_status !== "acessado").length})</TabsTrigger>
        </TabsList>

        {/* ============ TAB IMPORTAR ============ */}
        <TabsContent value="importar" className="mt-4 space-y-4">
          {/* PASSO 1: Empresa + Tipo + Arquivo */}
          {bloqueadoOnboarding && (
            <div className="mb-4 border border-[#E60000] bg-[#FEF2F2] p-4" data-testid="imp-onboarding-bloqueio">
              <div className="text-sm font-bold text-[#B91C1C]">Complete o cadastro da empresa antes de importar</div>
              <ul className="mt-2 space-y-1">
                {onboarding.pendencias.map(p => (
                  <li key={p.key} className="text-xs text-[#7F1D1D]">
                    • {p.label} — <Link to={p.rota} className="underline font-bold">resolver</Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {bloqueadoPublicacao && (
            <div className="mb-4 border-2 border-[#FF6700] bg-orange-50 p-4" data-testid="imp-publicacao-bloqueio">
              <div className="text-sm font-bold text-[#142766]">Publique suas condições antes de importar planilhas</div>
              <p className="text-xs text-muted-foreground mt-1">
                A importação envia mensagens aos seus clientes. O fluxo correto é: publicar as condições para a rede
                → Montadoras e Montadores aceitam → importar a planilha. Suas condições ainda <strong>não foram publicadas</strong>.
              </p>
              <Link to="/admin/condicoes" className="inline-block mt-2 text-xs font-bold underline text-[#FF6700]"
                data-testid="imp-publicacao-cta">Ir para Publicar Condições →</Link>
            </div>
          )}
          <div className="border border-border bg-white p-6 space-y-5">
            <div className="label-cap text-[#142766]">Passo 1 · Selecione o arquivo</div>
            <div className="grid md:grid-cols-2 gap-4">
              {isEmpresa ? (
                <div>
                  <Label className="label-cap">Empresa contratante</Label>
                  <div className="mt-2 px-3 py-2 border border-border bg-[#F4F4F5] text-sm font-semibold" data-testid="imp-empresa-fixa">
                    {empresa?.nome || user?.name} <span className="text-xs text-muted-foreground font-normal">(sua empresa)</span>
                  </div>
                </div>
              ) : (
                <div>
                  <Label className="label-cap">Empresa contratante *</Label>
                  <Select value={empresaId} onValueChange={setEmpresaId}>
                    <SelectTrigger className="mt-2" data-testid="imp-empresa"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {empresas.length === 0 && <SelectItem value="_none" disabled>Cadastre uma empresa primeiro</SelectItem>}
                      {empresas.map((e) => <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="label-cap">Tipo de planilha *</Label>
                <Select value={tipoPlanilha} onValueChange={setTipoPlanilha}>
                  <SelectTrigger className="mt-2" data-testid="imp-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pedido">PEDIDOS (produto não entregue)</SelectItem>
                    <SelectItem value="atendimento">ATENDIMENTOS (produto já entregue)</SelectItem>
                    <SelectItem value="particular">CLIENTE PARTICULAR (ofertar montagem)</SelectItem>
                  </SelectContent>
                </Select>
                {tipoPlanilha === "particular" && (
                  <div className="mt-3 border border-[#FFCC00] bg-[#FFFBEB] p-3 text-sm" data-testid="imp-particular-aviso">
                    A planilha de <strong>Cliente Particular</strong> tem fluxo próprio (oferta via WhatsApp, contrato e pagamento).
                    <Link to="/admin/cliente-particular" className="block mt-2 font-bold underline text-aj-blue-deep"
                      data-testid="imp-particular-link">
                      → Ir para Cliente Particular e importar lá
                    </Link>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="label-cap">Arquivo CSV ou Excel (XLSX/XLSM)</Label>
              <div className="mt-2 border-2 border-dashed border-border p-8 text-center">
                <input type="file" accept=".csv,.txt,.xlsx,.xlsm" onChange={(e) => { setFile(e.target.files?.[0]); setAnalise(null); }}
                  className="block mx-auto text-sm" data-testid="imp-arquivo" />
                {file && <div className="mt-3 text-sm font-mono">{file.name}</div>}
              </div>
            </div>

            <div className="flex items-center justify-between text-xs">
              <button onClick={baixarExemplo} className="underline text-muted-foreground" data-testid="imp-exemplo">Baixar exemplo padrão</button>
              <Button onClick={analisar} disabled={loading || !empresaId || !file}
                className="btn-sharp brand-bg text-white hover:bg-[#001A5E]" data-testid="imp-analisar">
                {loading ? "Analisando…" : "Analisar colunas →"}
              </Button>
            </div>
          </div>

          {/* PASSO 2: Mapeamento (aparece após análise) */}
          {analise && (
            <div className="border border-border bg-white p-6 space-y-5" data-testid="imp-mapeamento">
              <div className="flex items-center justify-between">
                <div>
                  <div className="label-cap text-[#142766]">Passo 2 · Mapeie as colunas</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {analise.colunas.length} colunas detectadas · {analise.total_linhas} linhas · separador <code>{analise.separador}</code>
                  </p>
                </div>
                {modelos.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Usar modelo:</Label>
                    <Select value={modeloSelecionado} onValueChange={aplicarModelo}>
                      <SelectTrigger className="w-[220px] h-9" data-testid="imp-select-modelo"><SelectValue placeholder="Mapeamento manual" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE_OPTION}>Mapeamento manual</SelectItem>
                        {modelos.map(m => <SelectItem key={m.id} value={m.id}>{m.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {analise.agrupamento && (
                <div className="border-2 border-[#142766] bg-blue-50 p-3 text-xs" data-testid="previa-agrupamento">
                  <div className="font-bold text-[#142766]">
                    Prévia do agrupamento: {analise.agrupamento.linhas_validas} linha(s) → {analise.agrupamento.os_geradas} OS
                    {analise.agrupamento.clientes_agrupados.length > 0 &&
                      ` · ${analise.agrupamento.clientes_agrupados.length} cliente(s) com itens agrupados`}
                  </div>
                  {analise.agrupamento.clientes_agrupados.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {analise.agrupamento.clientes_agrupados.slice(0, 10).map((g, i) => (
                        <li key={i} data-testid={`previa-grupo-${i}`}>
                          • <strong>{g.nome}</strong>: {g.itens} itens em 1 única OS/link
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-1 text-muted-foreground">
                    Produtos do mesmo cliente (mesa + cadeiras etc.) viram uma única OS com um único link/WhatsApp.
                  </div>
                </div>
              )}

              {/* Tabela de mapeamento (agrupada por bloco) */}
              {(() => {
                const blocos = {
                  atendimento: { label: "1 · Atendimento", color: "bg-[#142766]/10" },
                  cliente: { label: "2 · Cliente", color: "bg-[#00A859]/10" },
                  endereco: { label: "3 · Endereço", color: "bg-[#FFCC00]/10" },
                  produto: { label: "4 · Produto", color: "bg-purple-500/10" },
                  garantia: { label: "5 · Garantia / Sinistro", color: "bg-orange-500/10" },
                };
                const grupos = {};
                campos.forEach(c => {
                  const k = c.bloco || "cliente";
                  if (!grupos[k]) grupos[k] = [];
                  grupos[k].push(c);
                });
                return (
                  <div className="border border-border space-y-0">
                    {Object.entries(blocos).map(([blocoKey, blocoMeta]) => (
                      <div key={blocoKey}>
                        <div className={`px-3 py-2 label-cap text-xs border-b border-border ${blocoMeta.color}`}>
                          {blocoMeta.label}
                        </div>
                        <table className="w-full text-sm">
                          <tbody>
                            {(grupos[blocoKey] || []).map(c => {
                              const v = mapeamento[c.key];
                              const sample = v && analise.preview[0] ? analise.preview[0][v] : "";
                              return (
                                <tr key={c.key} className="border-b border-border last:border-0">
                                  <td className="p-2.5 w-1/3">
                                    <div className="font-medium text-xs">{c.label}</div>
                                    {c.obrigatorio && <span className="text-[9px] text-red-600 font-bold">OBRIGATÓRIO</span>}
                                    {c.critico && !c.obrigatorio && <span className="text-[9px] text-amber-600 font-bold">CRÍTICO</span>}
                                  </td>
                                  <td className="p-2.5 w-2/5">
                                    <Select value={v || NONE_OPTION} onValueChange={(val) => setMap(c.key, val)}>
                                      <SelectTrigger className="h-8 text-xs" data-testid={`map-${c.key}`}><SelectValue placeholder="Não mapeado" /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value={NONE_OPTION}>— Não mapear —</SelectItem>
                                        {analise.colunas.map(col => (
                                          <SelectItem key={col} value={col}>
                                            {col}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                  <td className="p-2.5 font-mono text-[11px] text-muted-foreground truncate max-w-[200px]">{sample || "—"}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Salvar modelo */}
              {!modeloSelecionado || modeloSelecionado === NONE_OPTION ? (
                <div className="border border-dashed border-border p-4 bg-[#F4F4F5]">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={salvarComoModelo} onChange={(e) => setSalvarComoModelo(e.target.checked)}
                      data-testid="imp-salvar-modelo" />
                    <strong>Salvar este mapeamento como modelo</strong> (reutilizável nas próximas importações)
                  </label>
                  {salvarComoModelo && (
                    <div className="mt-3 grid md:grid-cols-2 gap-3">
                      <Input placeholder="Nome do modelo (ex.: Planilha Magazine Luiza)" value={nomeModelo}
                        onChange={(e) => setNomeModelo(e.target.value)} data-testid="imp-modelo-nome" />
                      <Input placeholder="Descrição (opcional)" value={descricaoModelo}
                        onChange={(e) => setDescricaoModelo(e.target.value)} data-testid="imp-modelo-desc" />
                    </div>
                  )}
                </div>
              ) : null}

              <div className="flex gap-2">
                <Button onClick={() => conferirFinanceiro(true)} disabled={carregandoConf || !mapeamento.nome}
                  variant="outline"
                  className="flex-1 btn-sharp h-12 border-[#00A859] text-[#166534] hover:bg-[#DCFCE7]" data-testid="imp-conferir">
                  {carregandoConf ? "Calculando…" : "🧮 Conferir cálculos"}
                </Button>
                <Button onClick={verPreviewFicha} disabled={carregandoPreview || !mapeamento.nome}
                  variant="outline"
                  className="flex-1 btn-sharp h-12" data-testid="imp-preview-ficha">
                  {carregandoPreview ? "Carregando…" : "👁️ Ver prévia da ficha do montador"}
                </Button>
                <Button onClick={importar} disabled={loading || !mapeamento.cpf || !mapeamento.nome || !conferencia?.pode_importar}
                  className="flex-1 btn-sharp brand-bg text-white hover:bg-[#001A5E] h-12" data-testid="imp-enviar">
                  {loading ? "Importando…" : `Importar ${analise.total_linhas} linha(s) e gerar links`}
                </Button>
              </div>

              {conferencia && (
                <div className={`border-l-4 p-4 ${conferencia.pode_importar ? "border-[#00A859] bg-[#00A859]/5" : "border-[#E60000] bg-red-50"}`} data-testid="imp-conferencia">
                  <div className="font-heading font-bold text-sm mb-1">
                    {conferencia.pode_importar ? "✅ Conferência Financeira — tudo certo" : `⛔ Conferência Financeira — ${conferencia.erros.length} erro(s), importação bloqueada`}
                  </div>
                  {conferencia.tipos_nao_identificados?.length > 0 && (
                    <div className="text-xs text-red-700 font-semibold mb-2" data-testid="imp-tipos-nao-ident">
                      {conferencia.tipos_nao_identificados.map((t) => (
                        <div key={t}>Serviço [{t}] não identificado na Tabela de Valores. Por favor, cadastre ou mapeie este termo.</div>
                      ))}
                    </div>
                  )}
                  {conferencia.duplicidades?.length > 0 && (
                    <div className="text-xs bg-[#FEF3C7] border border-[#F59E0B]/40 rounded p-2 mb-2 space-y-1.5" data-testid="imp-duplicidades">
                      <div className="font-bold text-[#92400E]">⚠ Possível assistência já existente — confirme antes de importar:</div>
                      {conferencia.duplicidades.map((l) => (
                        <div key={l.linha} className="flex items-center gap-2 flex-wrap">
                          <span>Linha {l.linha} · {l.nome} · {l.duplicidade.pim_code} ({l.duplicidade.status})</span>
                          <select value={dupDecisoes[l.duplicidade.chave] || ""}
                            onChange={(e) => setDupDecisoes(d => ({ ...d, [l.duplicidade.chave]: e.target.value }))}
                            className="border border-border rounded px-2 py-1 text-xs bg-white"
                            data-testid={`imp-dup-${l.linha}`}>
                            <option value="">Decidir…</option>
                            <option value="vincular">Pertence a este cliente — vincular à OS existente</option>
                            <option value="nova">Não pertence — criar OS nova</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="overflow-x-auto max-h-72 overflow-y-auto border border-border bg-white">
                    {(() => {
                      const amb = conferencia.linhas.filter(l => l.valor_ambiguo).length;
                      const n = Object.keys(correcoesPreenchidas()).length;
                      return amb >= 2 ? (
                        <div className="flex items-center justify-between gap-2 px-3 py-2 bg-[#FFFBEB] border-b border-[#FDE68A] text-xs" data-testid="imp-correcoes-lote">
                          <span>{amb} linha(s) com valor ambíguo · {n} correção(ões) digitada(s)</span>
                          <Button size="sm" onClick={() => conferirFinanceiro(true)} disabled={carregandoConf || n === 0}
                            className="btn-sharp h-7 text-[10px] brand-bg text-white" data-testid="imp-aplicar-correcoes-lote">
                            {carregandoConf ? "Aplicando…" : `Aplicar todas as correções (${n})`}
                          </Button>
                        </div>
                      ) : null;
                    })()}
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-[#142766] text-white">
                        <tr>
                          <th className="p-2 text-left">Nº Atendimento</th>
                          <th className="p-2 text-left">Cliente</th>
                          <th className="p-2 text-left">Tipo recebido</th>
                          <th className="p-2 text-left">Serviço reconhecido</th>
                          <th className="p-2 text-left">Regra da Empresa</th>
                          <th className="p-2 text-right">Valor-base</th>
                          <th className="p-2 text-right">Repasse Montador</th>
                          <th className="p-2 text-right">Repasse Montadora</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conferencia.linhas.map((l) => (
                          <tr key={l.linha} className={`border-t border-border ${l.erro ? "bg-red-50" : ""}`} data-testid={`conf-linha-${l.linha}`}>
                            <td className="p-2">{l.numero_atendimento || `linha ${l.linha}`}</td>
                            <td className="p-2">{l.nome || "—"}</td>
                            <td className="p-2" data-testid={`conf-tipo-recebido-${l.linha}`}>{l.tipo_servico}</td>
                            <td className="p-2" data-testid={`conf-servico-reconhecido-${l.linha}`}>
                              {l.tipo_servico_match ? (
                                <span className="font-semibold">{l.tipo_servico_match}</span>
                              ) : (
                                <span className="text-red-700 font-bold">SERVIÇO NÃO RECONHECIDO — CONFERIR</span>
                              )}
                              {l.erro && <div className="text-red-700 font-semibold mt-0.5">{l.erro}</div>}
                              {l.valor_ambiguo && (
                                <div className="mt-1 flex items-center gap-2" data-testid={`conf-correcao-${l.linha}`}>
                                  <Input value={valorCorrecoes[l.linha] || ""}
                                    onChange={(e) => setValorCorrecoes(c => ({ ...c, [l.linha]: e.target.value }))}
                                    placeholder="Valor correto (ex.: 1.019,00)"
                                    className="h-7 w-44 text-xs bg-white"
                                    data-testid={`conf-correcao-input-${l.linha}`} />
                                  <Button size="sm" onClick={() => conferirFinanceiro(true)} disabled={carregandoConf || !String(valorCorrecoes[l.linha] || "").trim()}
                                    className="btn-sharp h-7 text-[10px] brand-bg text-white" data-testid={`conf-correcao-aplicar-${l.linha}`}>
                                    Aplicar correção
                                  </Button>
                                </div>
                              )}
                              {l.valor_corrigido && (
                                <div className="text-[10px] font-bold text-[#00784A] mt-1" data-testid={`conf-corrigido-${l.linha}`}>
                                  ✓ valor corrigido na grade (original: {l.valor_original})
                                </div>
                              )}
                            </td>
                            <td className="p-2" data-testid={`conf-regra-${l.linha}`}>
                              {l.tipo_servico_match
                                ? (l.modo === "percentual"
                                    ? `Percentual — ${Number(l.regra_valor ?? 0)}%`
                                    : `Valor fixo — R$ ${Number(l.regra_valor ?? 0).toFixed(2)}`)
                                : "—"}
                            </td>
                            <td className="p-2 text-right">R$ {Number(l.valor_base).toFixed(2)}</td>
                            <td className="p-2 text-right font-semibold">{l.erro ? "—" : `R$ ${Number(l.v_montador).toFixed(2)}`}</td>
                            <td className="p-2 text-right">{l.erro ? "—" : `R$ ${Number(l.v_montadora).toFixed(2)}`}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="border-t-2 border-[#142766] font-bold bg-slate-50">
                        <tr>
                          <td className="p-2" colSpan={5}>TOTAIS ({conferencia.totais.linhas} linha(s))</td>
                          <td className="p-2 text-right">R$ {Number(conferencia.totais.valor_base).toFixed(2)}</td>
                          <td className="p-2 text-right">R$ {Number(conferencia.totais.v_montador).toFixed(2)}</td>
                          <td className="p-2 text-right">R$ {Number(conferencia.totais.v_montadora).toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {resultado && (
                <div className="border-l-4 border-[#00A859] bg-[#00A859]/5 p-4 text-sm" data-testid="imp-resultado">
                  <strong>Importação concluída.</strong> {resultado.linhas_importadas} linha(s) importadas.
                  {resultado.erros?.length > 0 && (
                    <div className="mt-2 text-xs text-red-700">
                      {resultado.erros.length} linha(s) com erro:
                      <ul className="list-disc pl-5 mt-1">
                        {resultado.erros.slice(0, 5).map((er, i) => (
                          <li key={i}>Linha {er.linha}: {er.motivo}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ============ TAB MODELOS ============ */}
        <TabsContent value="modelos" className="mt-4">
          <div className="border border-border bg-white">
            {!empresaId && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                Selecione uma empresa na aba <strong>Importar</strong> para ver seus modelos.
              </div>
            )}
            {empresaId && modelos.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">
                Nenhum modelo salvo para esta empresa. Após mapear sua primeira planilha, marque <em>"Salvar como modelo"</em>.
              </div>
            )}
            {modelos.map(m => (
              <div key={m.id} className="border-b border-border last:border-0 p-5" data-testid={`modelo-${m.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="font-semibold">{m.nome}</div>
                    {m.descricao && <div className="text-xs text-muted-foreground mt-1">{m.descricao}</div>}
                    <div className="text-[11px] text-muted-foreground mt-2">
                      Criado {new Date(m.criado_em).toLocaleDateString("pt-BR")} · usado {m.usado_vezes || 0}× ·
                      {Object.keys(m.mapeamento || {}).length} campos mapeados
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {Object.entries(m.mapeamento || {}).map(([k, v]) => (
                        <span key={k} className="text-[10px] bg-secondary px-2 py-0.5">
                          <strong>{k}</strong> ← {v}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="outline" className="btn-sharp h-8"
                      onClick={() => setEditandoModelo({ ...m })} data-testid={`modelo-edit-${m.id}`}>Editar</Button>
                    <Button size="sm" variant="outline" className="btn-sharp h-8 text-red-700"
                      onClick={() => removerModelo(m.id)} data-testid={`modelo-del-${m.id}`}>Remover</Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* ============ TAB PENDENTES ============ */}
        <TabsContent value="pendentes" className="mt-4">
          <div className="border border-border bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-[#F4F4F5]">
                <tr><Th>Cliente</Th><Th>Produto</Th><Th>Tipo</Th><Th>Link</Th><Th>Status</Th><Th>Ações</Th></tr>
              </thead>
              <tbody>
                {pendentes.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Nenhuma planilha importada</td></tr>}
                {pendentes.map((p) => (
                  <tr key={p.id} className="border-b border-border last:border-0" data-testid={`plan-${p.id}`}>
                    <Td><strong>{p.nome}</strong><div className="text-xs text-muted-foreground">{p.telefone}</div></Td>
                    <Td>{p.produto} ({p.quantidade})</Td>
                    <Td>
                      <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider ${p.tipo_planilha === "atendimento" ? "bg-[#00A859] text-white" : "bg-secondary"}`}>
                        {p.tipo_planilha || "pedido"}
                      </span>
                    </Td>
                    <Td>
                      <a href={`/atendimento/${p.token}`} target="_blank" rel="noreferrer"
                        className="font-mono text-xs underline truncate block max-w-[200px]" data-testid={`plan-link-${p.id}`}>
                        /atendimento/{p.token?.slice(0, 12)}…
                      </a>
                    </Td>
                    <Td>
                      <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-wider ${STATUS_COLORS[p.link_status] || "bg-secondary"}`}>
                        {p.link_status === "alerta_envio" ? "⚠ alerta" : (p.link_status || "gerado")}
                      </span>
                      {p.link_status === "alerta_envio" && p.alerta_envio_motivo && (
                        <div className="text-[10px] text-[#E60000] font-semibold mt-1" data-testid={`plan-alerta-${p.id}`}>
                          {MOTIVO_ALERTA[p.alerta_envio_motivo] || p.alerta_envio_motivo}
                        </div>
                      )}
                    </Td>
                    <Td>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="btn-sharp h-7 text-[10px]" onClick={() => copiarMsg(p)} data-testid={`plan-copiar-${p.id}`}>
                          Copiar msg
                        </Button>
                        <Button size="sm" className="btn-sharp brand-bg text-white h-7 text-[10px]" onClick={() => enviarWhats(p)} data-testid={`plan-whats-${p.id}`}>
                          WhatsApp
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog Prévia Ficha Montador */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>👁️ Prévia · É assim que o montador verá o atendimento</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-4 text-sm">
              {/* Alertas */}
              {(preview.alertas?.obrigatorios_faltando?.length > 0 || preview.alertas?.criticos_faltando?.length > 0) && (
                <div className="border-l-4 border-amber-500 bg-amber-50 p-3 text-xs space-y-1">
                  {preview.alertas.obrigatorios_faltando?.length > 0 && (
                    <div><strong>Obrigatórios faltando:</strong> {preview.alertas.obrigatorios_faltando.join(", ")}</div>
                  )}
                  {preview.alertas.criticos_faltando?.length > 0 && (
                    <div><strong>Críticos faltando:</strong> {preview.alertas.criticos_faltando.join(", ")} (o montador pode ter dificuldade para executar)</div>
                  )}
                </div>
              )}

              {/* Cabeçalho */}
              <FichaBlock title="📋 Cabeçalho" rows={[
                ["Nº Atendimento", preview.ficha.cabecalho.numero_atendimento],
                ["Tipo de Serviço", preview.ficha.cabecalho.tipo_servico],
                ["Empresa", preview.ficha.cabecalho.empresa],
                ["Cliente", preview.ficha.cabecalho.cliente],
                ["Telefone Principal", preview.ficha.cabecalho.telefone_principal],
                ["Telefone Secundário", preview.ficha.cabecalho.telefone_secundario],
              ]} />

              <FichaBlock title="📍 Endereço" rows={[
                ["Rua / Logradouro", preview.ficha.endereco.rua],
                ["Número", preview.ficha.endereco.numero],
                ["Complemento", preview.ficha.endereco.complemento],
                ["Bairro", preview.ficha.endereco.bairro],
                ["Cidade / UF", `${preview.ficha.endereco.cidade} / ${preview.ficha.endereco.estado}`],
                ["CEP", preview.ficha.endereco.cep],
                ["Referência", preview.ficha.endereco.referencia],
              ]} />

              <FichaBlock title="📦 Produto" rows={[
                ["Produto", preview.ficha.produto.produto],
                ["Código", preview.ficha.produto.codigo],
                ["Marca", preview.ficha.produto.marca],
                ["Modelo", preview.ficha.produto.modelo],
                ["Cor", preview.ficha.produto.cor],
                ["Quantidade", preview.ficha.produto.quantidade],
                ["Valor (R$)", preview.ficha.produto.valor],
              ]} />

              <FichaBlock title="🛡️ Garantia / Sinistro" rows={[
                ["Seguradora", preview.ficha.garantia.seguradora],
                ["Nº Sinistro", preview.ficha.garantia.sinistro],
                ["Defeito Reclamado", preview.ficha.garantia.defeito_reclamado],
                ["Peça Solicitada", preview.ficha.garantia.peca_solicitada],
                ["Observações", preview.ficha.garantia.observacoes],
              ]} />

              <div className="text-xs text-muted-foreground border-t border-border pt-3">
                Mostrando a <strong>1ª linha de {preview.total_linhas}</strong>. Se algo está em campo errado, ajuste o mapeamento e clique novamente em "Ver prévia".
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}>Voltar para mapear</Button>
            <Button onClick={() => { setPreview(null); importar(); }}
              className="brand-bg text-white" data-testid="preview-importar">
              Está correto · Importar agora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog edição de modelo */}
      <Dialog open={!!editandoModelo} onOpenChange={(o) => !o && setEditandoModelo(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Editar Modelo</DialogTitle></DialogHeader>
          {editandoModelo && (
            <div className="space-y-3">
              <div>
                <Label className="label-cap">Nome</Label>
                <Input value={editandoModelo.nome}
                  onChange={(e) => setEditandoModelo({ ...editandoModelo, nome: e.target.value })}
                  data-testid="edit-modelo-nome" />
              </div>
              <div>
                <Label className="label-cap">Descrição</Label>
                <Textarea value={editandoModelo.descricao || ""}
                  onChange={(e) => setEditandoModelo({ ...editandoModelo, descricao: e.target.value })}
                  data-testid="edit-modelo-desc" />
              </div>
              <div className="border border-border p-3 text-xs">
                <div className="label-cap mb-2">Mapeamento atual</div>
                <table className="w-full">
                  <tbody>
                    {campos.map(c => (
                      <tr key={c.key} className="border-b border-border last:border-0">
                        <td className="py-1 pr-3"><strong>{c.label}</strong></td>
                        <td className="py-1">
                          <Input className="h-8 text-xs" value={editandoModelo.mapeamento?.[c.key] || ""}
                            onChange={(e) => setEditandoModelo({
                              ...editandoModelo,
                              mapeamento: { ...editandoModelo.mapeamento, [c.key]: e.target.value }
                            })} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditandoModelo(null)}>Cancelar</Button>
            <Button onClick={salvarEdicaoModelo} className="brand-bg text-white" data-testid="edit-modelo-salvar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children }) { return <th className="text-left p-3 label-cap font-medium">{children}</th>; }
function Td({ children, className="" }) { return <td className={`p-3 ${className}`}>{children}</td>; }

function FichaBlock({ title, rows }) {
  return (
    <div className="border border-border">
      <div className="bg-[#F4F4F5] px-3 py-2 label-cap text-xs border-b border-border">{title}</div>
      <table className="w-full text-xs">
        <tbody>
          {rows.map(([k, v], i) => (
            <tr key={i} className="border-b border-border last:border-0">
              <td className="p-2 text-muted-foreground w-1/3">{k}</td>
              <td className={`p-2 font-medium ${v === "—" ? "text-muted-foreground/50" : ""}`}>{v ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
